import React, { useEffect, useState } from "react";
import { Edit, useForm, useSelect } from "@refinedev/antd";
import {
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  Select,
  Row,
  Col,
  Typography,
  Space,
  DatePicker,
  Alert
} from "antd";
import { message } from "antd";
import type { FormListFieldData, FormListOperation } from "antd/es/form/FormList";
import { MinusCircleOutlined, PlusOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../api";

const { Text } = Typography;

interface TaxOption {
  label: string;
  value: number;
  data: {
    id: number;
    taxType: string;
    rate: number;
    isActive: boolean;
  };
}

export const SaleOrderEdit: React.FC = () => {
  const [showBillableServices, setShowBillableServices] = useState(false);
  const [taxOptions, setTaxOptions] = useState<TaxOption[]>([]);
  const [standardTaxRate, setStandardTaxRate] = useState(15.5);
  const [formReady, setFormReady] = useState(false);
  const [originalItems, setOriginalItems] = useState<any[]>([]);
  const [currentItems, setCurrentItems] = useState<any[]>([]);

  const { form, formProps, saveButtonProps, queryResult } = useForm({
    resource: "saleorders",
    action: "edit",
    warnWhenUnsavedChanges: false,
    meta: {
      fields: [
        "id",
        "invoiceNo",
        "customerTPIN",
        "customerId",
        "customerName",
        "currencyTypeCode",
        "exchangeRate",
        "subTotal",
        "totalTaxableAmount",
        "totalTax",
        "totalAmount",
        "status",
        "orderDate",
        "deliveryDate",
        "notes",
        {
          items: [
            "id",
            "inventoryItemId",
            "itemCode",
            "itemName",
            "itemClassCode",
            "quantity",
            "taxRate",
            "unitPrice",
            "discountRate",
            "discountAmount",
            "totalAmount",
            "taxAmount",
            "lineAmount",
            "isActive",
            "priceId",
            "taxType"
          ]
        },
        {
          billableServices: [
            "id",
            "description",
            "price",
            "taxId",
            "taxRate",
            "taxType"
          ]
        }
      ],
    },
    successNotification: {
      message: "Sale order updated successfully",
      type: "success",
    },
  });

  const { data, isLoading } = queryResult;
  const record = data?.data?.value || data?.data;

  // Customer select
  const { selectProps: customerSelectProps } = useSelect({
    resource: "customers",
    optionLabel: "name",
    optionValue: "id",
    filters: [{ field: "isActive", operator: "eq", value: true }],
  });

  // Currency select
  const { selectProps: currencySelectProps } = useSelect({
    resource: "currency",
    optionLabel: "currencyCode",
    optionValue: "currencyCode",
  });

  // Item select
  const { selectProps: itemSelectProps, queryResult: itemQueryResult } = useSelect({
    resource: "inventory/available",
    optionLabel: "itemName",
    optionValue: "itemCode",
    meta: {
      fields: [
        "id",
        "itemCode",
        "itemName",
        "itemClassificationCode",
        "taxId",
        {
          tax: ["id", "taxType", "rate", "isActive"],
          prices: ["id", "basePrice", "effectivePrice", "discountType", "discountValue", "minimumQuantity", "maximumQuantity", "currencyCode", "isActive"],
        },
      ],
    },
  });

  // Fetch taxes
  useQuery({
    queryKey: ['taxes'],
    queryFn: () => api.getTaxes(),
    onSuccess: (data) => {
      const options = data.map((tax: any) => ({
        label: `${tax.taxType} (${tax.rate}%)`,
        value: tax.id,
        data: tax
      }));
      setTaxOptions(options);
      if (options.length > 0) {
        setStandardTaxRate(options[0].data.rate);
      }
    },
  });

  // Initialize form with existing data
  useEffect(() => {
    if (record && form && !formReady) {
      console.log("Loading sale order record:", record);

      // Transform items (will be updated when inventory data loads)
      const items = record.items?.map((item: any) => ({
        id: item.id,
        inventoryItemId: item.inventoryItemId, // Will be updated below if inventory data is available
        itemCode: item.itemCode,
        itemName: item.itemName,
        itemClassCode: item.itemClassCode,
        quantity: Number(item.quantity) || 1,
        unitPrice: Number(item.unitPrice) || 0,
        taxRate: Number(item.taxRate) || 0,
        discountRate: Number(item.discountRate) || 0,
        discountAmount: Number(item.discountAmount) || 0,
        totalAmount: Number(item.totalAmount) || 0,
        taxAmount: Number(item.taxAmount) || 0,
        lineAmount: Number(item.lineAmount) || 0,
        isActive: item.isActive ?? true,
        priceId: item.priceId,
        taxType: item.taxType,
      })) || [];

      // Transform billable services
      const billableServices = record.billableServices?.map((b: any) => ({
        id: b.id,
        description: b.description,
        price: Number(b.price) || 0,
        taxId: b.taxId,
        taxRate: Number(b.taxRate) || 0,
        taxType: b.taxType,
      })) || [];

      // Set form values
      form.resetFields();
      form.setFieldsValue({
        id: record.id,
        invoiceNo: record.invoiceNo,
        customerId: record.customerId,
        customerTPIN: record.customerTPIN || "0",
        customerName: record.customerName,
        currencyTypeCode: record.currencyTypeCode,
        exchangeRate: Number(record.exchangeRate) || 1,
        subTotal: Number(record.subTotal) || 0,
        totalTaxableAmount: Number(record.totalTaxableAmount) || 0,
        totalTax: Number(record.totalTax) || 0,
        totalAmount: Number(record.totalAmount) || 0,
        status: record.status,
        orderDate: record.orderDate ? dayjs(record.orderDate) : dayjs(),
        deliveryDate: record.deliveryDate ? dayjs(record.deliveryDate) : null,
        notes: record.notes,
        items: items,
        billableServices: billableServices,
      });

      setShowBillableServices(billableServices.length > 0);
      setOriginalItems(items);
      setCurrentItems([...items]); // Initialize currentItems with original data
      setFormReady(true);
    }
  }, [record, form, formReady]);

  // Update inventoryItemIds when inventory data becomes available
  useEffect(() => {
    if (record && form && formReady && itemQueryResult?.data?.data && currentItems.length > 0 && currentItems.some(item => !item.inventoryItemId)) {
      console.log("Updating inventoryItemIds with inventory data");

      const inventoryItems = itemQueryResult.data.data;
      const updatedItems = currentItems.map((item: any) => {
        // Only update if inventoryItemId is not already set
        if (!item.inventoryItemId) {
          const inventoryItem = inventoryItems.find((inv: any) => inv.itemCode === item.itemCode);
          const inventoryItemId = inventoryItem?.id;

          console.log(`Updating item ${item.itemCode}: inventoryItemId ${item.inventoryItemId} -> ${inventoryItemId}`);

          return {
            ...item,
            inventoryItemId: inventoryItemId,
          };
        }
        return item;
      });

      setCurrentItems(updatedItems);
      form.setFieldsValue({ items: updatedItems });
    }
  }, [record, form, formReady, itemQueryResult, currentItems]);

  // Handle item selection
  const handleItemSelect = async (value: string, name: number) => {
    if (!itemQueryResult?.data?.data) {
      console.warn("itemQueryResult not available yet");
      return;
    }

    const itemData = itemQueryResult.data.data.find(
      (item: any) => item.itemCode === value
    );

    if (itemData) {
      const currentItems = form.getFieldValue('items') || [];

      // Get all active prices for the item
      const activePrices = itemData.prices?.filter((price: any) =>
        price.isActive
      ) || [];

      if (activePrices.length === 0) {
        // No active prices found
        message.warning(`No active prices found for ${itemData.itemName}`);
        return;
      }

      // Auto-select the first active price (or default price if available)
      const defaultPrice = activePrices.find((price: any) => price.isDefault) || activePrices[0];

      // Calculate discount rate based on price record
      let discountRate = 0;
      if (defaultPrice?.discountType === "Percentage") {
        discountRate = defaultPrice.discountValue / 100;
      } else if (defaultPrice?.discountType === "FixedAmount" && defaultPrice.basePrice > 0) {
        discountRate = defaultPrice.discountValue / defaultPrice.basePrice;
      }

      const updatedItems = [...currentItems];
      updatedItems[name] = {
        ...updatedItems[name],
        inventoryItemId: itemData.id,
        itemCode: itemData.itemCode,
        itemName: itemData.itemName,
        itemClassCode: itemData.itemClassificationCode,
        unitPrice: defaultPrice?.effectivePrice || 0,
        taxRate: itemData.tax?.rate || 0,
        taxType: itemData.tax?.taxType || "VAT",
        discountRate: discountRate,
        priceId: defaultPrice?.id || null,
        quantity: updatedItems[name]?.quantity || 1,
        isActive: true
      };

      setCurrentItems(updatedItems); // Update our current items state
      form.setFieldsValue({
        items: updatedItems
      });

      // Update line amounts and totals
      updateLineAmounts(name);

      // Show info message about price selection
      if (activePrices.length > 1) {
        message.info(`${itemData.itemName}: Auto-selected default price. ${activePrices.length} pricing options available.`);
      } else if (defaultPrice) {
        message.success(`${itemData.itemName}: Price set to ${defaultPrice.currencyCode} ${defaultPrice.effectivePrice.toFixed(2)}`);
      }
    }
  };

  // Update line amounts for an item
  const updateLineAmounts = (name: number) => {
    const currentItems = form.getFieldValue('items') || [];
    const currentItem = currentItems[name];
    if (!currentItem) return;

    const quantity = Number(currentItem.quantity) || 0;
    const unitPrice = Number(currentItem.unitPrice) || 0;
    const taxRate = (Number(currentItem.taxRate) || 0) / 100;
    const discountRate = Number(currentItem.discountRate) || 0;

    const lineAmount = quantity * unitPrice;
    const discountAmount = lineAmount * discountRate;
    const totalAmount = lineAmount - discountAmount;
    const taxDivisor = 1 + taxRate;
    const taxableAmount = taxDivisor !== 0 ? totalAmount / taxDivisor : 0;
    const taxAmount = totalAmount - taxableAmount;

    const updatedCurrentItems = [...currentItems];
    updatedCurrentItems[name] = {
      ...currentItem,
      lineAmount,
      discountAmount,
      taxAmount,
      totalAmount
    };

    setCurrentItems(updatedCurrentItems);
    form.setFieldsValue({ items: updatedCurrentItems });
    updateTotals();
  };

  // Update all totals
  const updateTotals = () => {
    const items = currentItems;
    const services = form.getFieldValue('billableServices') || [];

    let totalTaxableAmount = 0;
    let totalTax = 0;
    let totalAmount = 0;

    items.forEach((item: any) => {
      const itemTotal = Number(item.totalAmount) || 0;
      const itemTaxAmount = Number(item.taxAmount) || 0;
      const itemTaxableAmount = Number(item.lineAmount) || (itemTotal - itemTaxAmount);

      totalAmount += itemTotal;
      totalTax += itemTaxAmount;
      totalTaxableAmount += itemTaxableAmount;
    });

    services.forEach((service: any) => {
      const price = Number(service.price) || 0;
      const taxRate = (Number(service.taxRate) || standardTaxRate) / 100;
      const taxDivisor = 1 + taxRate;
      const taxable = taxDivisor !== 0 ? price / taxDivisor : 0;
      const tax = price - taxable;

      totalAmount += price;
      totalTax += tax;
      totalTaxableAmount += taxable;
    });

    form.setFieldsValue({
      subTotal: totalTaxableAmount,
      totalTaxableAmount,
      totalTax,
      totalAmount,
    });
  };

  // Handle customer selection
  const handleCustomerSelect = (customerId: number) => {
    const customer = customerSelectProps.data?.find((c: any) => c.id === customerId);
    if (customer) {
      form.setFieldsValue({
        customerId: customer.id,
        customerTPIN: customer.tpin || "0",
        customerName: customer.name
      });
    }
  };

  // Check if editing is allowed
  const canEdit = record?.status === "Draft";

  if (isLoading || !formReady) {
    return <div>Loading...</div>;
  }

  if (!canEdit && record) {
    return (
      <Edit saveButtonProps={{ ...saveButtonProps, disabled: true }}>
        <Alert
          message="Sale order cannot be edited"
          description={`This sale order has status "${record?.status}". Only sale orders with "Draft" status can be edited.`}
          type="warning"
          showIcon
        />
      </Edit>
    );
  }

  return (
    <Edit saveButtonProps={saveButtonProps}>
      <Form
        {...formProps}
        layout="vertical"
        onFinish={(values) => {
          console.log("DEBUG - currentItems:", currentItems);
          console.log("DEBUG - values.items:", values.items);

          const submitData = {
            id: record?.id,
            invoiceNo: values.invoiceNo,
            customerTPIN: values.customerTPIN || "0",
            customerId: values.customerId,
            customerName: values.customerName,
            currencyTypeCode: values.currencyTypeCode,
            exchangeRate: Number(values.exchangeRate) || 1,
            totalItemCount: (values.items || []).length,
            totalTaxableAmount: Number(values.totalTaxableAmount) || 0,
            totalTax: Number(values.totalTax) || 0,
            totalAmount: Number(values.totalAmount) || 0,
            orderDate: values.orderDate?.format?.('YYYY-MM-DD') || dayjs().format('YYYY-MM-DD'),
            deliveryDate: values.deliveryDate?.format?.('YYYY-MM-DD') || null,
            notes: values.notes,
            items: currentItems.map((item: any) => ({
              inventoryItemId: Number(item.inventoryItemId),
              quantity: Number(item.quantity) || 0,
              taxRate: Number(item.taxRate) || 0,
              unitPrice: Number(item.unitPrice) || 0,
              discountRate: Number(item.discountRate) || 0,
              discountAmount: Number(item.discountAmount) || 0,
              totalAmount: Number(item.totalAmount) || 0,
              isActive: true,
            })),
            billableServices: (values.billableServices || []).map((service: any) => ({
              description: service.description,
              price: Number(service.price) || 0,
              taxId: service.taxId || taxOptions[0]?.value,
              taxRate: Number(service.taxRate) || standardTaxRate,
              taxType: service.taxType || taxOptions[0]?.data?.taxType || "VAT",
            })),
          };

          console.log("Submitting sale order update:", submitData);
          formProps.onFinish?.(submitData);
        }}
      >
        {/* Hidden ID field */}
        <Form.Item name="id" hidden><Input /></Form.Item>

        <Row gutter={16}>
          {/* Left Column */}
          <Col span={16}>
            {/* Sale Order Details */}
            <Card title="Sale Order Details" size="small" style={{ marginBottom: 16 }}>
              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item label="Invoice Number" name="invoiceNo" rules={[{ required: true }]}>
                    <Input readOnly />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item label="Order Date" name="orderDate" rules={[{ required: true }]}>
                    <DatePicker style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item label="Delivery Date" name="deliveryDate">
                    <DatePicker style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item label="Currency" name="currencyTypeCode" rules={[{ required: true }]}>
                    <Select {...currencySelectProps} />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item label="Exchange Rate" name="exchangeRate" rules={[{ required: true }]}>
                    <InputNumber min={0} step={0.01} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
              </Row>
            </Card>

            {/* Customer Details */}
            <Card title="Customer" size="small" style={{ marginBottom: 16 }}>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item label="Select Customer" name="customerId">
                    <Select
                      {...customerSelectProps}
                      onChange={handleCustomerSelect}
                      allowClear
                      placeholder="Select a customer"
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="Customer TPIN" name="customerTPIN">
                    <Input />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={16}>
                <Col span={24}>
                  <Form.Item label="Customer Name" name="customerName" rules={[{ required: true }]}>
                    <Input />
                  </Form.Item>
                </Col>
              </Row>
            </Card>

            {/* Items */}
            <Card title="Items" size="small" style={{ marginBottom: 16 }}>
              <Form.List name="items">
                {(fields: FormListFieldData[], { add, remove }: FormListOperation) => (
                  <>
                    {fields.map(({ key, name, ...restField }) => (
                      <Card key={key} size="small" style={{ marginBottom: 8, backgroundColor: '#fafafa' }}>
                        <Row gutter={8}>
                          <Col span={6}>
                            <Form.Item {...restField} label="Item" name={[name, 'itemCode']} rules={[{ required: true }]}>
                              <Select
                                {...itemSelectProps}
                                placeholder="Select item"
                                showSearch
                                onChange={(value) => handleItemSelect(value as string, name)}
                              />
                            </Form.Item>
                          </Col>
                          <Col span={3}>
                            <Form.Item {...restField} label="Qty" name={[name, 'quantity']} rules={[{ required: true }]}>
                              <InputNumber min={1} style={{ width: '100%' }} onChange={() => updateLineAmounts(name)} />
                            </Form.Item>
                          </Col>
                          <Col span={4}>
                            <Form.Item {...restField} label="Unit Price" name={[name, 'unitPrice']}>
                              <InputNumber min={0} precision={2} style={{ width: '100%' }} onChange={() => updateLineAmounts(name)} />
                            </Form.Item>
                          </Col>
                          <Col span={3}>
                            <Form.Item {...restField} label="Tax %" name={[name, 'taxRate']}>
                              <InputNumber min={0} max={100} style={{ width: '100%' }} onChange={() => updateLineAmounts(name)} />
                            </Form.Item>
                          </Col>
                          <Col span={3}>
                            <Form.Item {...restField} label="Disc %" name={[name, 'discountRate']}>
                              <InputNumber min={0} max={1} step={0.01} style={{ width: '100%' }} onChange={() => updateLineAmounts(name)} />
                            </Form.Item>
                          </Col>
                          <Col span={4}>
                            <Form.Item {...restField} label="Total" name={[name, 'totalAmount']}>
                              <InputNumber readOnly style={{ width: '100%' }} />
                            </Form.Item>
                          </Col>
                          <Col span={1}>
                            <Form.Item label=" ">
                              <Button type="text" danger icon={<MinusCircleOutlined />} onClick={() => remove(name)} />
                            </Form.Item>
                          </Col>
                        </Row>
                        {/* Hidden fields */}
                        <Form.Item name={[name, 'id']} hidden><Input /></Form.Item>
                        <Form.Item name={[name, 'inventoryItemId']} hidden><Input /></Form.Item>
                        <Form.Item name={[name, 'itemName']} hidden><Input /></Form.Item>
                        <Form.Item name={[name, 'itemClassCode']} hidden><Input /></Form.Item>
                        <Form.Item name={[name, 'priceId']} hidden><Input /></Form.Item>
                        <Form.Item name={[name, 'taxType']} hidden><Input /></Form.Item>
                        <Form.Item name={[name, 'lineAmount']} hidden><Input /></Form.Item>
                        <Form.Item name={[name, 'discountAmount']} hidden><Input /></Form.Item>
                        <Form.Item name={[name, 'taxAmount']} hidden><Input /></Form.Item>
                        <Form.Item name={[name, 'isActive']} hidden><Input /></Form.Item>
                      </Card>
                    ))}
                    <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                      Add Item
                    </Button>
                  </>
                )}
              </Form.List>
            </Card>

            {/* Billable Services */}
            {!showBillableServices ? (
              <Button type="dashed" onClick={() => setShowBillableServices(true)} block style={{ marginBottom: 16 }}>
                Add Billable Services
              </Button>
            ) : (
              <Card title="Billable Services" size="small" style={{ marginBottom: 16 }}>
                <Form.List name="billableServices">
                  {(fields: FormListFieldData[], { add, remove }: FormListOperation) => (
                    <>
                      {fields.map(({ key, name, ...restField }) => (
                        <Row key={key} gutter={8} style={{ marginBottom: 8 }}>
                          <Col span={10}>
                            <Form.Item {...restField} name={[name, 'description']} rules={[{ required: true }]}>
                              <Input placeholder="Description" />
                            </Form.Item>
                          </Col>
                          <Col span={5}>
                            <Form.Item {...restField} name={[name, 'price']} rules={[{ required: true }]}>
                              <InputNumber min={0} placeholder="Price" style={{ width: '100%' }} />
                            </Form.Item>
                          </Col>
                          <Col span={7}>
                            <Form.Item {...restField} name={[name, 'taxId']}>
                              <Select
                                options={taxOptions}
                                placeholder="Tax"
                              />
                            </Form.Item>
                          </Col>
                          <Col span={2}>
                            <Button type="text" danger icon={<MinusCircleOutlined />} onClick={() => remove(name)} />
                          </Col>
                          <Form.Item name={[name, 'id']} hidden><Input /></Form.Item>
                          <Form.Item name={[name, 'taxRate']} hidden><InputNumber /></Form.Item>
                          <Form.Item name={[name, 'taxType']} hidden><Input /></Form.Item>
                        </Row>
                      ))}
                      <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                        Add Service
                      </Button>
                    </>
                  )}
                </Form.List>
              </Card>
            )}

            {/* Notes */}
            <Card title="Additional Information" size="small">
              <Form.Item label="Notes" name="notes">
                <Input.TextArea rows={2} />
              </Form.Item>
            </Card>
          </Col>

          {/* Right Column - Summary */}
          <Col span={8}>
            <Card title="Summary" size="small">
              <Form.Item label="Status" name="status">
                <Input readOnly />
              </Form.Item>

              <Form.Item shouldUpdate={(prev: any, cur: any) =>
                prev.totalTaxableAmount !== cur.totalTaxableAmount ||
                prev.totalTax !== cur.totalTax ||
                prev.totalAmount !== cur.totalAmount ||
                prev.currencyTypeCode !== cur.currencyTypeCode
              } noStyle>
                {({ getFieldValue }: any) => {
                  const currency = getFieldValue('currencyTypeCode') || 'USD';
                  return (
                    <Space direction="vertical" size={8} style={{ width: '100%', marginTop: 16 }}>
                      <Row justify="space-between">
                        <Text>Taxable Amount:</Text>
                        <Text>{currency} {(getFieldValue('totalTaxableAmount') || 0).toFixed(2)}</Text>
                      </Row>
                      <Row justify="space-between">
                        <Text>Tax:</Text>
                        <Text>{currency} {(getFieldValue('totalTax') || 0).toFixed(2)}</Text>
                      </Row>
                      <Row justify="space-between">
                        <Text strong>Total:</Text>
                        <Text strong>{currency} {(getFieldValue('totalAmount') || 0).toFixed(2)}</Text>
                      </Row>
                    </Space>
                  );
                }}
              </Form.Item>

              {/* Hidden fields */}
              <Form.Item name="subTotal" hidden><InputNumber /></Form.Item>
            </Card>
          </Col>
        </Row>
      </Form>
    </Edit>
  );
};